import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Search,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  UserPlus,
  MessageSquare,
  DollarSign,
  Calendar,
  Download,
  Loader2,
  ChevronDown,
  ChevronUp,
  Package,
  TrendingUp,
  Flag,
  AlertTriangle,
  CheckCircle2,
  Eye as EyeIcon,
  Send,
  Phone,
  PackageOpen,
  Truck,
  X,
} from "lucide-react";
import { requestService } from "../../api/services/requestService";
import { format } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";

// Helper Methods
const parseProducts = (products) => {
  if (!products) return [];
  if (Array.isArray(products)) return products;
  if (typeof products === "string") {
    try {
      const parsed = JSON.parse(products);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing products:", e);
      return [];
    }
  }
  return [];
};

const getStatusStyles = (status) => {
  const styles = {
    pending:
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
    contacted:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800",
    quoted:
      "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800",
    approved:
      "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800",
    completed:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800",
    cancelled:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
    rejected:
      "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800",
  };
  return styles[status?.toLowerCase()] || styles.pending;
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, trend }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-gray-800/50 shadow-lg"
  >
    <div
      className={`absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5`}
    />
    <div className="relative p-5">
      <div className="flex items-center justify-between mb-3">
        <div
          className={`p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-md`}
        >
          <Icon size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value?.toLocaleString() || 0}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </p>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs">
          <TrendingUp size={12} className="text-emerald-500" />
          <span className="text-emerald-600 dark:text-emerald-400">
            +{trend}%
          </span>
          <span className="text-gray-500">vs last month</span>
        </div>
      )}
    </div>
    <div
      className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500`}
    />
  </motion.div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: {
      icon: Clock,
      label: "Pending",
      color: "text-amber-600",
      bg: "bg-amber-50 dark:bg-amber-900/20",
      border: "border-amber-200 dark:border-amber-800",
    },
    contacted: {
      icon: Phone,
      label: "Contacted",
      color: "text-blue-600",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    quoted: {
      icon: DollarSign,
      label: "Quoted",
      color: "text-purple-600",
      bg: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-200 dark:border-purple-800",
    },
    approved: {
      icon: CheckCircle,
      label: "Approved",
      color: "text-emerald-600",
      bg: "bg-emerald-50 dark:bg-emerald-900/20",
      border: "border-emerald-200 dark:border-emerald-800",
    },
    completed: {
      icon: CheckCircle2,
      label: "Completed",
      color: "text-green-600",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    cancelled: {
      icon: XCircle,
      label: "Cancelled",
      color: "text-red-600",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
    rejected: {
      icon: AlertTriangle,
      label: "Rejected",
      color: "text-rose-600",
      bg: "bg-rose-50 dark:bg-rose-900/20",
      border: "border-rose-200 dark:border-rose-800",
    },
  };
  const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.bg} ${config.color} border ${config.border}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const priorityConfig = {
    low: {
      icon: Flag,
      label: "Low",
      color: "text-gray-600",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    medium: {
      icon: Flag,
      label: "Medium",
      color: "text-blue-600",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    high: {
      icon: AlertTriangle,
      label: "High",
      color: "text-orange-600",
      bg: "bg-orange-50 dark:bg-orange-900/20",
      border: "border-orange-200 dark:border-orange-800",
    },
    urgent: {
      icon: AlertCircle,
      label: "Urgent",
      color: "text-red-600",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
  };
  const config =
    priorityConfig[priority?.toLowerCase()] || priorityConfig.medium;
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.bg} ${config.color} border ${config.border}`}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Product Item Component
const ProductItem = ({ product, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.05 }}
    className="flex items-start gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700"
  >
    <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500/10 to-secondary-500/10">
      <PackageOpen size={16} className="text-primary-500" />
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-900 dark:text-white">
        {product.productName || "Product"}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        ID: {product.productId || "N/A"}
      </p>
      {product.notes && (
        <p className="text-xs text-gray-400 mt-1 italic">
          <MessageSquare className="w-4 h-4 inline-flex" /> {product.notes}
        </p>
      )}
    </div>
  </motion.div>
);

// Request Row Component
const RequestRow = ({
  request,
  isExpanded,
  onToggleExpand,
  onSelect,
  isSelected,
  onViewDetails,
  onAddResponse,
  onDelete,
  onAssign,
  onStatusChange,
}) => {
  const products = parseProducts(request.products);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  const handleStatusChange = async (newStatus) => {
    if (newStatus === request.status) return;

    setIsUpdatingStatus(true);
    try {
      // Send lowercase status as expected by the API enum
      await requestService.updateRequestStatus(
        request.id,
        newStatus.toLowerCase(),
      );
      toastSuccess(`Request status updated to ${newStatus}`);
      onStatusChange?.(); // Refresh parent
    } catch (err) {
      toastError(err.message || "Failed to update status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors group"
      >
        <td className="px-4 py-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(request.id)}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        </td>
        <td className="px-4 py-3">
          <span className="text-sm font-mono font-semibold text-primary-600 dark:text-primary-400">
            #{request.requestNumber}
          </span>
        </td>
        <td className="px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {request.company ||
                request.user?.company ||
                "Individual Customer"}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {request.name || `${request.firstName} ${request.lastName}`}
            </p>
          </div>
        </td>
        <td className="px-4 py-3">
          <button
            onClick={() => onToggleExpand(request.id)}
            className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <Package size={14} />
            <span>{products.length} items</span>
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </td>
        <td className="px-4 py-3">
          <div className="relative">
            <select
              value={request.status?.toLowerCase() || "pending"}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={isUpdatingStatus}
              className={`appearance-none pl-2.5 pr-8 py-1 text-xs font-medium rounded-full border cursor-pointer transition-all ${
                isUpdatingStatus ? "opacity-50" : ""
              } ${getStatusStyles(request.status)}`}
            >
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="quoted">Quoted</option>
              <option value="approved">Approved</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
            {isUpdatingStatus ? (
              <Loader2
                size={12}
                className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin"
              />
            ) : (
              <ChevronDown
                size={12}
                className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
              />
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <PriorityBadge priority={request.priority} />
        </td>
        <td className="px-4 py-3">
          {request.assignedTo ? (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {request.assignedTo.firstName?.charAt(0)}
                </span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {request.assignedTo.firstName} {request.assignedTo.lastName}
              </span>
            </div>
          ) : (
            <button
              onClick={() => onAssign(request)}
              className="text-xs text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1 transition-colors"
            >
              <UserPlus size={14} />
              Assign
            </button>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {format(new Date(request.createdAt), "MMM d, yyyy")}
            </span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onViewDetails(request)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="View Details"
            >
              <EyeIcon size={16} className="text-blue-500" />
            </button>
            <button
              onClick={() => onAddResponse(request)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Add Response"
            >
              <MessageSquare size={16} className="text-green-500" />
            </button>
            <button
              onClick={() => onDelete(request.id)}
              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Delete"
            >
              <Trash2 size={16} className="text-red-500" />
            </button>
          </div>
        </td>
      </motion.tr>
      {isExpanded && (
        <tr className="bg-gray-50/50 dark:bg-gray-800/30">
          <td colSpan={10} className="px-4 py-4">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Package size={14} className="text-primary-500" />
                Requested Products ({products.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {products.map((product, idx) => (
                  <ProductItem key={idx} product={product} index={idx} />
                ))}
              </div>
              {request.message && (
                <div className="mt-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Customer Message</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {request.message}
                  </p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Details Modal Component
const DetailsModal = ({ request, onClose }) => {
  const products = parseProducts(request.products);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Request Details
            </h2>
            <p className="text-sm font-mono text-primary-600 dark:text-primary-400 mt-0.5">
              #{request.requestNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex flex-wrap gap-4">
            <StatusBadge status={request.status} />
            <PriorityBadge priority={request.priority} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl">
            <div>
              <p className="text-xs text-gray-500">Customer Name</p>
              <p className="text-sm font-medium">
                {request.name || `${request.firstName} ${request.lastName}`}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm">{request.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm">{request.phone || "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Company</p>
              <p className="text-sm">{request.company || "N/A"}</p>
            </div>
          </div>

          {request.message && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Message</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {request.message}
              </p>
            </div>
          )}

          {products.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Package size={14} /> Products ({products.length})
              </h3>
              <div className="space-y-2">
                {products.map((p, i) => (
                  <ProductItem key={i} product={p} index={i} />
                ))}
              </div>
            </div>
          )}

          {request.deliveryAddress && (
            <div className="p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Truck size={14} /> Delivery Info
              </h3>
              <p className="text-sm">
                {request.deliveryAddress}, {request.deliveryCity},{" "}
                {request.deliveryCountry} {request.deliveryPostalCode}
              </p>
            </div>
          )}

          {request.adminResponse && (
            <div className="p-4 bg-primary-50/50 dark:bg-primary-900/20 rounded-xl">
              <h3 className="text-sm font-semibold text-primary-600 mb-2 flex items-center gap-2">
                <MessageSquare size={14} /> Admin Response
              </h3>
              <p className="text-sm">{request.adminResponse}</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Response Modal Component
const ResponseModal = ({ request, onClose, onSuccess }) => {
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const { success: toastSuccess, error: toastError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!response.trim()) {
      setError("Response message is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Save response to database
      await requestService.addAdminResponse(request.id, response);

      // Send email to customer if email exists and toggle is on
      if (sendEmail && request.email) {
        try {
          await requestService.sendResponseEmail(
            request.id,
            response,
            request.email,
          );
          toastSuccess("Response sent and email delivered successfully!");
        } catch (emailError) {
          console.error("Error sending email:", emailError);
          toastSuccess("Response saved, but email sending failed");
        }
      } else {
        toastSuccess("Response sent successfully");
      }

      onSuccess();
    } catch (err) {
      console.error("Error submitting response:", err);
      toastError(err.message || "Failed to send response");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Add Response
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Request #{request.requestNumber}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Customer Email Info */}
        {request.email && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Customer Email
            </p>
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-blue-500" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {request.email}
              </span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Response Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={5}
              className="w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
              placeholder="Type your response to the customer..."
              required
            />
          </div>

          {/* Email Toggle */}
          {request.email && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Send Email to Customer
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Customer will receive this response via email
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSendEmail(!sendEmail)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
                    sendEmail
                      ? "bg-primary-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                      sendEmail ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </label>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2 hover:from-primary-600 hover:to-secondary-600 transition-all shadow-lg"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : sendEmail ? (
                <>
                  <Send size={18} />
                  Send & Email
                </>
              ) : (
                <>
                  <MessageSquare size={18} />
                  Save Response
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Assign Modal Component
const AssignModal = ({ request, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await requestService.assignRequest(request.id);
      toastSuccess("Request assigned successfully");
      onSuccess();
    } catch (err) {
      toastError(err.message || "Failed to assign request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-2">Assign Request</h2>
        <p className="text-sm text-gray-500 mb-4">
          Request #{request.requestNumber}
        </p>
        <p className="text-sm mb-4">This request will be assigned to you.</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <UserPlus size={18} />
            )}
            {loading ? "Assigning..." : "Assign to Me"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const RequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [
    statusFilter,
    priorityFilter,
    sortBy,
    sortOrder,
    currentPage,
    searchTerm,
  ]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        // Send lowercase status values to match API enum
        ...(statusFilter !== "all" && { status: statusFilter.toLowerCase() }),
        ...(priorityFilter !== "all" && {
          priority: priorityFilter.toLowerCase(),
        }),
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await requestService.getAllRequests(params);
      let requestsData = [];
      if (response?.data?.data) requestsData = response.data.data;
      else if (response?.data?.requests) requestsData = response.data.requests;
      else if (Array.isArray(response?.data)) requestsData = response.data;
      else if (response?.requests) requestsData = response.requests;
      setRequests(requestsData || []);
      setTotalRequests(
        response?.data?.pagination?.total || requestsData.length,
      );
      setTotalPages(response?.data?.pagination?.totalPages || 1);
    } catch (err) {
      toastError(err.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await requestService.getAllRequests({ limit: 100 });
      console.log("Stats API Response:", response);

      let requestsData = [];

      // Handle different response structures
      if (response?.data?.data && Array.isArray(response.data.data)) {
        requestsData = response.data.data;
      } else if (
        response?.data?.requests &&
        Array.isArray(response.data.requests)
      ) {
        requestsData = response.data.requests;
      } else if (Array.isArray(response?.data)) {
        requestsData = response.data;
      } else if (response?.requests && Array.isArray(response.requests)) {
        requestsData = response.requests;
      } else if (Array.isArray(response)) {
        requestsData = response;
      }

      console.log("Extracted requests data for stats:", requestsData);

      // Calculate stats from the requests data
      const total = requestsData.length;
      const pending = requestsData.filter((r) => r.status === "pending").length;
      const contacted = requestsData.filter(
        (r) => r.status === "contacted",
      ).length;
      const quoted = requestsData.filter((r) => r.status === "quoted").length;
      const approved = requestsData.filter(
        (r) => r.status === "approved",
      ).length;
      const completed = requestsData.filter(
        (r) => r.status === "completed",
      ).length;
      const cancelled = requestsData.filter(
        (r) => r.status === "cancelled",
      ).length;
      const rejected = requestsData.filter(
        (r) => r.status === "rejected",
      ).length;

      console.log("Calculated stats:", {
        total,
        pending,
        contacted,
        quoted,
        approved,
        completed,
        cancelled,
        rejected,
      });

      setStats({
        total,
        pending,
        inReview: contacted,
        inProgress: quoted,
        approved,
        completed,
        cancelled,
        rejected,
        thisMonth: 0,
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
      // Set default stats to avoid undefined errors
      setStats({
        total: 0,
        pending: 0,
        inReview: 0,
        approved: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        rejected: 0,
        thisMonth: 0,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await requestService.deleteRequest(id);
      toastSuccess("Request deleted");
      fetchRequests();
      fetchStats();
      setSelectedRequests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (err) {
      toastError("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRequests.size) return;
    if (!window.confirm(`Delete ${selectedRequests.size} request(s)?`)) return;
    try {
      await requestService.bulkDeleteRequests(Array.from(selectedRequests));
      toastSuccess(`${selectedRequests.size} request(s) deleted`);
      setSelectedRequests(new Set());
      setSelectAll(false);
      fetchRequests();
      fetchStats();
    } catch (err) {
      toastError("Failed to delete requests");
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (!selectedRequests.size) return;
    try {
      // Send lowercase status
      await requestService.bulkUpdateStatus(
        Array.from(selectedRequests),
        status.toLowerCase(),
      );
      toastSuccess(`${selectedRequests.size} request(s) updated to ${status}`);
      setSelectedRequests(new Set());
      setSelectAll(false);
      fetchRequests();
      fetchStats();
    } catch (err) {
      toastError("Failed to update statuses");
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedRequests.size) return;
    try {
      await requestService.bulkAssignToAdmin(Array.from(selectedRequests));
      toastSuccess(`${selectedRequests.size} request(s) assigned`);
      setSelectedRequests(new Set());
      setSelectAll(false);
      fetchRequests();
    } catch (err) {
      toastError("Failed to assign requests");
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedRequests(
      selectAll ? new Set() : new Set(requests.map((r) => r.id)),
    );
  };
  const handleSelectRequest = (id) => {
    const newSet = new Set(selectedRequests);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedRequests(newSet);
    setSelectAll(newSet.size === requests.length);
  };
  const toggleRowExpand = (id) => {
    const newSet = new Set(expandedRows);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setExpandedRows(newSet);
  };

  const statCards = [
    {
      icon: Mail,
      label: "Total Requests",
      value: stats?.total,
      trend: 12,
    },
    {
      icon: Clock,
      label: "Pending",
      value: stats?.pending,
    },
    {
      icon: EyeIcon,
      label: "In Review",
      value: stats?.inReview,
    },
    {
      icon: CheckCircle,
      label: "Approved",
      value: stats?.approved,
    },
    {
      icon: CheckCircle2,
      label: "Completed",
      value: stats?.completed,
    },
  ];

  if (loading && !requests.length)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-500">Loading requests...</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Requests
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage customer inquiries and requests
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {}}
              className="px-4 py-2 bg-white dark:bg-gray-900 border rounded-xl flex items-center gap-2"
            >
              <Download size={18} /> Export
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <StatCard key={i} {...card} />
          ))}
        </div>
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by request #, company, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="quoted">Quoted</option>
              <option value="approved">Approved</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <button
              onClick={fetchRequests}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border hover:bg-gray-100"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
        {selectedRequests.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold">
              {selectedRequests.size} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkStatusUpdate("approved")}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg"
              >
                Approve
              </button>
              <button
                onClick={() => handleBulkStatusUpdate("in_progress")}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg"
              >
                In Progress
              </button>
              <button
                onClick={handleBulkAssign}
                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg"
              >
                Assign
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}
        {error && (
          <div className="p-4 bg-red-50 rounded-2xl flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-200/50 dark:border-gray-700/50">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Request #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Products
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Assigned To
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <RequestRow
                    key={req.id}
                    request={req}
                    isExpanded={expandedRows.has(req.id)}
                    onToggleExpand={toggleRowExpand}
                    onSelect={handleSelectRequest}
                    isSelected={selectedRequests.has(req.id)}
                    onViewDetails={(r) => {
                      setSelectedRequest(r);
                      setShowDetailsModal(true);
                    }}
                    onAddResponse={(r) => {
                      setSelectedRequest(r);
                      setShowResponseModal(true);
                    }}
                    onDelete={handleDelete}
                    onAssign={(r) => {
                      setSelectedRequest(r);
                      setShowAssignModal(true);
                    }}
                    onStatusChange={() => {
                      fetchRequests();
                      fetchStats();
                    }}
                  />
                ))}
              </tbody>
            </table>
            {!requests.length && (
              <div className="text-center py-12">
                <Mail size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-gray-500">No requests found</p>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-3 py-1.5">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <DetailsModal
            request={selectedRequest}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showResponseModal && selectedRequest && (
          <ResponseModal
            request={selectedRequest}
            onClose={() => {
              setShowResponseModal(false);
              setSelectedRequest(null);
            }}
            onSuccess={() => {
              fetchRequests();
              setShowResponseModal(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAssignModal && selectedRequest && (
          <AssignModal
            request={selectedRequest}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedRequest(null);
            }}
            onSuccess={() => {
              fetchRequests();
              setShowAssignModal(false);
              setSelectedRequest(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default RequestsManagement;
